import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { trackAttendeeGenerationFinish } from '../../analytics';

import { EstimatedProgress } from '../../components/custom';
import { PageWrapper } from '../../components/layout/content';
import { ErrorModal } from '../../components/modals';

import { getOneCreation } from '../../services/api/creations';
import { getEventPublic } from '../../services/api/events';
import { getAttendeeReviewData } from '../../services/videoBuilderService';

import { Person, PageError } from '../../types';
import { goTo, isEmpty, getShareLink } from '../../utils';

const ReviewVideoGenerating = () => {
  const { faceId = '' } = useParams();

  const [eventId, setEventId] = useState('');
  const [person, setPerson] = useState<Person | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<PageError>(null);
  const [error, setError] = useState('');

  // handle data fetching and initialization on mount
  useEffect(() => {
    const onMount = async () => {
      setIsLoading(true);

      await fetchData();
    };

    onMount();
  }, [faceId]);

  // fetch attendee, event & creation data
  const fetchData = async () => {
    try {
      const { person, eventId } = await getAttendeeReviewData(faceId);
      const event = await getEventPublic(eventId);

      if (isEmpty(person) || isEmpty(event)) {
        setPageError(404);
        return;
      }

      // check if creation is already generated
      const creation = await getOneCreation(faceId);
      if (!!creation) {
        goTo(getShareLink(eventId, faceId));
        return;
      }

      setEventId(eventId);
      setPerson(person);

      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setError('Error fetching some data');
    }
  };

  // re-fetch every 10 seconds
  useEffect(() => {
    const generationInterval = setInterval(async () => {
      const creation = await getOneCreation(faceId);

      // if video is generated – redirect to Share Video page
      if (creation) {
        trackAttendeeGenerationFinish();
        goTo(getShareLink(eventId, faceId));
        clearInterval(generationInterval);
      }
    }, 10000);

    return () => clearInterval(generationInterval);
  }, [eventId, faceId]);

  const tipsList = [
    'Your video is being assembled in the cosmic workshop. Just a few more lightyears!',
    'Stitching together your interstellar memories—almost ready for launch!',
    'Your video is orbiting the digital universe and will land shortly',
    'Our space engineers are fine-tuning your video at warp speed. Hold tight!',
    'Creating your masterpiece among the stars. It’ll be worth the wait!',
    'The countdown has begun. Your video is fueling up for the final launch!',
    'Gathering cosmic dust to sprinkle some magic on your video. Almost there!',
    'Your video is passing through a wormhole of creativity. Hang in there!',
    'Our satellites are compiling your video footage from across the galaxy!',
    'Your video is on a mission to become out of this world. Preparing for re-entry!',
  ];

  return (
    <PageWrapper
      type="header-only"
      title="Generating Your Video"
      isLoading={isLoading}
      pageError={pageError}
      className="items-center justify-center"
      noTips
    >
      <EstimatedProgress
        heading="Generating your video"
        expectedMin={15}
        tipsList={tipsList}
        progressName={`${person?.face?.name} personalised video`}
        className="w-full sm:w-2/3"
      />

      <ErrorModal
        show={!!error}
        heading="Something went wrong"
        subheading={error}
        isTryAgainBtn
        isContactUs
      />
    </PageWrapper>
  );
};

export default ReviewVideoGenerating;
